/**
 * Work List PDF — 가로 A4. `WorkListA4PreviewHorizontal`와 동일한 페이지·행·열 규칙.
 */

import React from 'react';
import { Document, Page, View, StyleSheet, Image } from '@react-pdf/renderer';
import {
  convertWorkSizeToString,
  SelectedData,
} from '../../shared/dto/EntityRepository';
import { CurrencyCode, WorkContentMode } from '../../shared/enum/EnumRepository';
import { renderingPrice } from '../../shared/util/renderingPrice';
import { resolveWorkMaterial, resolveWorkTitle } from '../../shared/util/workDisplayText';
import { CvPdfText } from '../about/cvPdfShared';
import logoFit from '../../asset/gnb/logo_fit.png';
import { convertS3UrlToCloudFrontUrl } from '../../shared/aws/awsHelper';
import {
  convertToJsonMetadata,
  generateOptimizedUrl,
  isDeviceAvailable,
} from '../../shared/util/findAdjustImg';
import {
  getWorkListPageStartIndex,
  getWorkListPdfTotalPageCount,
  getWorkListRowsOnPage,
} from './WorkListA4PreviewHorizontal';
import { ensureHttpThumbUriForReactPdf } from '../../shared/util/workListExportThumbRaster';
import { resolveWorkArtistName } from '../../shared/util/workArtist';

const A4_LANDSCAPE: [number, number] = [842, 595];

/** 1페이지만: 제목·부제 블록 아래에서 테이블이 시작되도록 상단 여백 */
const WORK_LIST_FIRST_PAGE_TABLE_TOP_PT = 108;
/** 2페이지 이후: 제목 없음 — 페이지 상단·로고와 겹치지 않을 최소 여백 */
const WORK_LIST_CONTINUATION_PAGE_TABLE_TOP_PT = 44;
const WORK_LIST_FOOTER_RESERVE_PT = 40;
/** 미리보기 행 높이 대비 PDF에서 약 3배 */
const ROW_HEIGHT_PT = 34;
/** `WorkListA4PreviewHorizontal`의 `column-gap: 0.8vh`에 맞춘 열 간격(pt) — Title/Year 등 인접 텍스트 붙음 방지 */
const WORK_LIST_TABLE_COLUMN_GAP_PT = 20;
const FIRST_PAGE_TABLE_EXTRA_PT = ROW_HEIGHT_PT * 0.5;

const asTrimmedString = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value).trim();
  return '';
};

const dashIfEmpty = (value: string): string => (value ? value : '-');

export const workListImageSource = (src: string): string | { uri: string } => {
  const resolved = convertS3UrlToCloudFrontUrl(asTrimmedString(src));
  if (resolved.startsWith('blob:') || resolved.startsWith('http') || resolved.startsWith('data:')) {
    return { uri: resolved };
  }
  if (typeof window !== 'undefined' && resolved.startsWith('/')) {
    return { uri: `${window.location.origin}${resolved}` };
  }
  return resolved;
};

const IMAGE_FILE_NAME_RE = /\/[^/?#]+\.(?:jpeg|jpg|png|gif|webp)((?:\?[^#]*)?(?:#.*)?)$/i;
const WORK_LIST_IMAGE_DEVICE_PRIORITY = ['THUMBNAIL', 'MOBILE', 'TABLET', 'LAPTOP', 'DESKTOP'] as const;

const resolveWorkListThumbSrc = async (originUrl: string): Promise<string> => {
  const trimmedOriginUrl = asTrimmedString(originUrl);
  if (!trimmedOriginUrl) return '';

  const baseUrl = convertS3UrlToCloudFrontUrl(trimmedOriginUrl);
  if (!baseUrl.startsWith('http') || !IMAGE_FILE_NAME_RE.test(baseUrl)) return baseUrl;

  try {
    const jsonUrl = baseUrl.replace(IMAGE_FILE_NAME_RE, '/meta.json');
    const response = await fetch(jsonUrl, { cache: 'no-cache' });
    if (!response.ok) throw new Error('Failed to fetch metadata');
    const contentType = response.headers.get('content-type');
    if (!contentType?.includes('application/json')) throw new Error('Response is not JSON');

    const metadata = convertToJsonMetadata(await response.json());
    for (const deviceType of WORK_LIST_IMAGE_DEVICE_PRIORITY) {
      if (isDeviceAvailable(metadata, deviceType)) {
        return generateOptimizedUrl(baseUrl, deviceType, metadata);
      }
    }
  } catch {
    // Ignore metadata failure and keep using origin/base URL.
  }

  return baseUrl;
};

export type WorkListPdfTableRow = {
  /** Excel 이중 언어 행에서 원문 행에만 작품 번호를 표시한다. */
  displayNo?: number | string;
  thumbSrc: string;
  title: string;
  artist: string;
  year: string;
  material: string;
  sizeLine: string;
  /** title · artist · year · material · size 를 한 줄로 합친 문자열 */
  captionLine: string;
  priceLine: string;
  noteLine: string;
};

export type WorkListExportColumnOpts = {
  includeArtist: boolean;
  includePrice: boolean;
  includeNote: boolean;
};

/** 제목, 작가, 연도, 재료, 크기를 한 줄 캡션으로 합친다. (Excel보내기용) */
export const formatWorkListCaptionOneLine = (
  row: Pick<WorkListPdfTableRow, 'title' | 'artist' | 'year' | 'material' | 'sizeLine'>,
  includeArtist = true,
): string => {
  const part = (value: string) => {
    const trimmed = asTrimmedString(value);
    return trimmed && trimmed !== '-' ? trimmed : '';
  };
  const parts = [
    part(row.title),
    ...(includeArtist ? [part(row.artist)] : []),
    part(row.year),
    part(row.material),
    part(row.sizeLine),
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : '-';
};

export const getWorkListHeaderLabels = (
  opts: WorkListExportColumnOpts,
): string[] => {
  const { includeArtist, includePrice, includeNote } = opts;
  return [
    'No.',
    'Image',
    ...(includeArtist ? ['Artist'] : []),
    'Title',
    'Year',
    'Material',
    'Size',
    ...(includePrice ? ['Price'] : []),
    ...(includeNote ? ['Note'] : []),
  ];
};

export const getWorkListPreviewColumnTemplate = (
  includeArtist: boolean,
  includePrice: boolean,
  includeNote: boolean,
): string => {
  const columns = includeArtist
    ? ['4fr', '9fr', '12fr', '18fr', '7fr', '18fr', '14fr']
    : ['4fr', '10fr', '20fr', '8fr', '20fr', '15fr'];
  if (includePrice) columns.push('8fr');
  if (includeNote) columns.push('10fr');
  return columns.join(' ');
};

/** 기본 열 이후 선택 열 인덱스 */
export const getWorkListOptionalColIndices = (
  includeArtist: boolean,
  includePrice: boolean,
  includeNote: boolean,
) => {
  let idx = includeArtist ? 7 : 6;
  const price = includePrice ? idx++ : -1;
  const note = includeNote ? idx++ : -1;
  return { price, note };
};

export const buildWorkListPdfTableRows = async (
  selectedDataList: SelectedData[],
  workContentMode: WorkContentMode = WorkContentMode.Orig,
  workEngVisible = true,
  includeThumbs = true,
  includeArtist = true,
): Promise<WorkListPdfTableRow[]> => {
  const thumbSrcCache = new Map<string, string>();
  const rows: WorkListPdfTableRow[] = [];
  for (const sel of selectedDataList) {
    const originUrl = asTrimmedString(sel.originUrl);
    let thumbSrc = '';
    if (includeThumbs && originUrl) {
      const cached = thumbSrcCache.get(originUrl);
      if (cached) {
        thumbSrc = cached;
      } else {
        let resolved = await resolveWorkListThumbSrc(originUrl);
        if (resolved && /^https?:\/\//i.test(resolved)) {
          resolved = await ensureHttpThumbUriForReactPdf(resolved);
        }
        thumbSrcCache.set(originUrl, resolved);
        thumbSrc = resolved;
      }
    }
    const sizeLine = (() => {
      if (asTrimmedString(sel.duration)) return asTrimmedString(sel.duration);
      if (!sel.size || !sel.dimensionsType) return '-';
      return convertWorkSizeToString(
        sel.size,
        sel.dimensionsType,
        asTrimmedString(sel.sizeText),
        'Variable',
      );
    })();

    const priceLine = (() => {
      if (sel.isPOV) return 'POA';
      if (sel.price == null || sel.price.amount === 0) return '-';
      const rendered = renderingPrice(sel.price);
      const formatted = asTrimmedString(rendered);
      if (!formatted) return '-';
      const currency = asTrimmedString(sel.price.currency).toUpperCase() || CurrencyCode.USD;
      return `${currency} ${formatted}`;
    })();

    rows.push({
      thumbSrc,
      title: dashIfEmpty(
        asTrimmedString(
          resolveWorkTitle(
            { title: sel.title ?? '', titleEn: sel.titleEn ?? '' },
            workContentMode,
            workEngVisible,
          ),
        ),
      ),
      artist: includeArtist
        ? dashIfEmpty(
            asTrimmedString(resolveWorkArtistName(sel.artist, workContentMode, workEngVisible)),
          )
        : '-',
      year: dashIfEmpty(asTrimmedString(sel.prodYear)),
      material: dashIfEmpty(
        asTrimmedString(
          resolveWorkMaterial(
            { material: sel.material ?? '', materialEn: sel.materialEn ?? '' },
            workContentMode,
            workEngVisible,
          ),
        ),
      ),
      sizeLine,
      captionLine: '',
      priceLine,
      noteLine: '',
    });
    rows[rows.length - 1].captionLine = formatWorkListCaptionOneLine(
      rows[rows.length - 1],
      includeArtist,
    );
  }
  return rows;
};

export const buildWorkListBilingualTableRows = async (
  selectedDataList: SelectedData[],
  includeArtist = true,
): Promise<WorkListPdfTableRow[]> => {
  const [originalRows, englishRows] = await Promise.all([
    buildWorkListPdfTableRows(selectedDataList, WorkContentMode.Orig, true, true, includeArtist),
    buildWorkListPdfTableRows(selectedDataList, WorkContentMode.ENG, true, false, includeArtist),
  ]);

  return originalRows.flatMap((originalRow, index) => [
    { ...originalRow, displayNo: index + 1 },
    { ...englishRows[index], displayNo: '', thumbSrc: '' },
  ]);
};

export const getWorkListPdfColumnFlexes = (
  includeArtist: boolean,
  includePrice: boolean,
  includeNote: boolean,
): number[] => {
  const flexes = includeArtist
    ? [2, 8, 12, 24, 6, 18, 12]
    : [2, 9, 30, 6, 18, 12];
  if (includePrice) flexes.push(8);
  if (includeNote) flexes.push(8);
  return flexes;
};

export interface DocumentWorkListHorizontalProps {
  title: string;
  subtitle: string;
  gallery: string;
  baseItemsPerPage: number;
  includeArtist: boolean;
  includePrice: boolean;
  includeNote: boolean;
  tableRows: WorkListPdfTableRow[];
}

const DocumentWorkListHorizontal: React.FC<DocumentWorkListHorizontalProps> = ({
  title,
  subtitle,
  gallery,
  baseItemsPerPage,
  includeArtist,
  includePrice,
  includeNote,
  tableRows,
}) => {
  const selectedCount = tableRows.length;
  const totalPages = getWorkListPdfTotalPageCount(selectedCount, baseItemsPerPage);
  const columnFlexes = getWorkListPdfColumnFlexes(includeArtist, includePrice, includeNote);

  const headerLabels = getWorkListHeaderLabels(
    { includeArtist, includePrice, includeNote },
  );
  const { price: priceColIndex, note: noteColIndex } =
    getWorkListOptionalColIndices(includeArtist, includePrice, includeNote);

  return (
    <Document>
      {Array.from({ length: totalPages }, (_, pageIdx) => {
        const pageNumber = pageIdx + 1;
        const startIndex = getWorkListPageStartIndex(baseItemsPerPage, pageNumber);
        const rowsOnPage = getWorkListRowsOnPage(baseItemsPerPage, pageNumber);
        /** 1페이지 테이블만 미리보기와 동일하게 추가로 내림(2행 분). 2페이지부터는 0. */
        const tableTopExtra = pageNumber === 1 ? FIRST_PAGE_TABLE_EXTRA_PT : 0;
        const tablePaddingTop =
          (pageNumber === 1 ? WORK_LIST_FIRST_PAGE_TABLE_TOP_PT : WORK_LIST_CONTINUATION_PAGE_TABLE_TOP_PT) +
          tableTopExtra;

        const slots: (WorkListPdfTableRow | null)[] = [];
        for (let r = 0; r < rowsOnPage; r += 1) {
          const globalIdx = startIndex + r;
          slots.push(globalIdx < tableRows.length ? tableRows[globalIdx] : null);
        }

        return (
          <Page key={`wl-pdf-p-${pageNumber}`} size={A4_LANDSCAPE} style={PDFStyles.Page.root}>
            <View style={PDFStyles.Section.pageBody}>
              <View
                style={[PDFStyles.Section.tableOuter, { paddingTop: tablePaddingTop }]}
              >
                <View style={PDFStyles.Section.tableHeaderRow}>
                  {headerLabels.map((label, i) => (
                    <View key={`h-${label}`} style={{ flex: columnFlexes[i], minWidth: 0 }}>
                      <CvPdfText style={PDFStyles.Text.headerCell}>{label}</CvPdfText>
                    </View>
                  ))}
                </View>
                {slots.map((row, rowIndex) =>
                  row ? (
                    <View key={`d-${startIndex + rowIndex}`} style={PDFStyles.Section.dataRow}>
                      <View style={{ flex: columnFlexes[0], minWidth: 0, justifyContent: 'center' }}>
                        <CvPdfText style={PDFStyles.Text.indexCell}>{`${startIndex + rowIndex + 1}.`}</CvPdfText>
                      </View>
                      <View
                        style={{
                          flex: columnFlexes[1],
                          minWidth: 0,
                          alignItems: 'flex-start',
                          justifyContent: 'center',
                        }}
                      >
                        {asTrimmedString(row.thumbSrc) ? (
                          <Image source={workListImageSource(row.thumbSrc)} style={PDFStyles.Image.thumb} />
                        ) : (
                          <View style={PDFStyles.Section.thumbPlaceholder} />
                        )}
                      </View>
                      {[
                        ...(includeArtist ? [row.artist] : []),
                        row.title,
                        row.year,
                        row.material,
                        row.sizeLine,
                      ].map((value, index) => (
                        <View
                          key={`core-${index}`}
                          style={{ flex: columnFlexes[index + 2], minWidth: 0, justifyContent: 'center' }}
                        >
                          <CvPdfText style={PDFStyles.Text.cell}>{value}</CvPdfText>
                        </View>
                      ))}
                      {includePrice && (
                        <View style={{ flex: columnFlexes[priceColIndex], minWidth: 0, justifyContent: 'center' }}>
                          <CvPdfText style={PDFStyles.Text.cell}>{row.priceLine}</CvPdfText>
                        </View>
                      )}
                      {includeNote && (
                        <View style={{ flex: columnFlexes[noteColIndex], minWidth: 0, justifyContent: 'center' }}>
                          <CvPdfText style={PDFStyles.Text.cell}>{row.noteLine}</CvPdfText>
                        </View>
                      )}
                    </View>
                  ) : (
                    <View key={`e-${startIndex + rowIndex}`} style={PDFStyles.Section.emptyRow}>
                      {columnFlexes.map((flex, ci) => (
                        <View
                          key={`ec-${startIndex + rowIndex}-${ci}`}
                          style={[PDFStyles.Section.emptyRowCell, { flex, minWidth: 0 }]}
                        />
                      ))}
                    </View>
                  )
                )}
              </View>
            </View>

            {pageNumber === 1 ? (
              <View style={PDFStyles.Section.page1HeaderLeft}>
                <CvPdfText style={PDFStyles.Text.titleLine}>{dashIfEmpty(title)}</CvPdfText>
                <CvPdfText style={PDFStyles.Text.metaLine}>{subtitle}</CvPdfText>
              </View>
            ) : null}

            <View style={PDFStyles.Section.fixedLogoWrap} fixed>
              <Image source={logoFit} style={PDFStyles.Image.logo} />
            </View>

            <View style={PDFStyles.Section.fixedFooterRow} fixed>
              <View style={PDFStyles.Section.footerCell}>
                <CvPdfText style={PDFStyles.Text.footerLeft}>{gallery}</CvPdfText>
              </View>
              <View style={PDFStyles.Section.footerCellRight}>
                <CvPdfText style={PDFStyles.Text.footerRight}>
                  {`${pageNumber} / ${totalPages}`}
                </CvPdfText>
              </View>
            </View>
          </Page>
        );
      })}
    </Document>
  );
};

const PDFTypography = {
  titleLine: { fontSize: 14, fontFamily: 'NotoKR', fontWeight: 600 },
  metaLine: { fontSize: 10, fontFamily: 'NotoKR', fontWeight: 400 },
  headerCell: { fontSize: 11, fontFamily: 'NotoKR', fontWeight: 600 },
  indexCell: { fontSize: 9, fontFamily: 'NotoKR', fontWeight: 500 },
  cell: { fontSize: 9, fontFamily: 'NotoKR', fontWeight: 400 },
  footer: { fontSize: 7, fontFamily: 'NotoKR', fontWeight: 400 },
};

const PDFStyles = {
  Page: StyleSheet.create({
    root: {
      paddingTop: 24,
      paddingBottom: 20,
      paddingLeft: 22,
      paddingRight: 22,
      backgroundColor: '#ffffff',
      fontFamily: 'NotoKR',
      fontWeight: 400,
    },
  }),

  Image: StyleSheet.create({
    logo: {
      width: 16,
      height: 16,
      objectFit: 'contain' as const,
    },
    thumb: {
      maxWidth: '100%',
      maxHeight: ROW_HEIGHT_PT - 4,
      objectFit: 'contain' as const,
      /** contain 시 기본 objectPosition이 가로 50%라 세로형 썸네일이 셀 안에서 가운데로 뜸 → 왼쪽 고정 */
      objectPosition: 'left center' as const,
    },
  }),

  Section: StyleSheet.create({
    pageBody: {
      flex: 1,
      width: '100%',
      minHeight: 0,
      flexDirection: 'column',
      paddingBottom: WORK_LIST_FOOTER_RESERVE_PT,
    },
    tableOuter: {
      width: '100%',
      flexDirection: 'column',
    },
    tableHeaderRow: {
      flexDirection: 'row',
      alignItems: 'center',
      columnGap: WORK_LIST_TABLE_COLUMN_GAP_PT,
      width: '100%',
      minHeight: ROW_HEIGHT_PT,
      borderBottomWidth: 0.75,
      borderBottomColor: '#000000',
      borderBottomStyle: 'solid',
      paddingBottom: 4,
    },
    dataRow: {
      flexDirection: 'row',
      alignItems: 'center',
      columnGap: WORK_LIST_TABLE_COLUMN_GAP_PT,
      width: '100%',
      minHeight: ROW_HEIGHT_PT,
      borderBottomWidth: 0.5,
      borderBottomColor: '#c7c7c7',
      borderBottomStyle: 'solid',
      paddingTop: 4,
      paddingBottom: 4,
    },
    emptyRow: {
      flexDirection: 'row',
      alignItems: 'stretch',
      columnGap: WORK_LIST_TABLE_COLUMN_GAP_PT,
      width: '100%',
      minHeight: ROW_HEIGHT_PT,
      borderBottomWidth: 0.5,
      borderBottomColor: '#cccccc',
      borderBottomStyle: 'solid',
    },
    emptyRowCell: {
      minHeight: ROW_HEIGHT_PT - 8,
      borderBottomWidth: 0,
    },
    thumbPlaceholder: {
      width: 32,
      height: 32,
    },
    /** `fixed` 쓰면 모든 페이지에 제목이 반복됨 — 1페이지 전용 절대 배치 */
    page1HeaderLeft: {
      position: 'absolute',
      top: 24,
      left: 22,
      maxWidth: '65%',
      flexDirection: 'column',
    },
    fixedLogoWrap: {
      position: 'absolute',
      top: 24,
      right: 22,
    },
    fixedFooterRow: {
      position: 'absolute',
      bottom: 20,
      left: 22,
      right: 22,
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
    },
    footerCell: {
      flex: 1,
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    footerCellRight: {
      flexShrink: 0,
      flexDirection: 'column',
      alignItems: 'flex-end',
      justifyContent: 'flex-end',
      minWidth: 72,
    },
  }),

  Text: StyleSheet.create({
    titleLine: { ...PDFTypography.titleLine, marginBottom: 1 },
    metaLine: { ...PDFTypography.metaLine },
    headerCell: { ...PDFTypography.headerCell },
    indexCell: { ...PDFTypography.indexCell },
    cell: { ...PDFTypography.cell },
    footerLeft: { ...PDFTypography.footer, textAlign: 'left' as const },
    footerRight: { ...PDFTypography.footer, textAlign: 'right' as const },
  }),
};

export default DocumentWorkListHorizontal;
